/**
 * MosaicDistrictRollup.js — v6.32.166
 *
 * Multi-class roll-up of the MOSAIC Compliance Snapshot. School-by-school
 * + class-by-class. Designed for District / School Admin dashboards.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import MosaicBucketBadge from './MosaicBucketBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_BG = { 1: 'bg-rose-300', 2: 'bg-orange-300', 3: 'bg-amber-300', 4: 'bg-emerald-300', 5: 'bg-blue-300' };

const DistroBar = ({ by_level = [], totalKey = 'count', screened = 0 }) => {
  const total = screened || by_level.reduce((s, l) => s + (l[totalKey] || 0), 0);
  if (total === 0) return <div className="h-2 bg-gray-100 border border-gray-200" />;
  return (
    <div className="flex h-2 border border-gray-200">
      {by_level.map((L) => {
        const pct = (L[totalKey] / total) * 100;
        if (pct === 0) return null;
        return <div key={L.level} style={{ width: `${pct}%` }} className={LEVEL_BG[L.level]} title={`${L.label}: ${L[totalKey]}`} />;
      })}
    </div>
  );
};

const MosaicDistrictRollup = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSchools, setOpenSchools] = useState({});

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/mosaic/compliance/district-rollup`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load district rollup');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="border-2 border-gray-900 bg-white p-5 flex items-center gap-3 text-gray-500" data-testid="mosaic-district-rollup">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading MOSAIC district rollup…
    </div>
  );
  if (error) return (
    <div className="border-2 border-rose-300 bg-rose-50 p-5 text-sm text-rose-800" data-testid="mosaic-district-rollup">
      {error}
    </div>
  );
  if (!data) return null;

  const grand = data.grand_total || {};

  return (
    <div className="border-2 border-gray-900 bg-white" data-testid="mosaic-district-rollup">
      <header className="flex items-center justify-between px-5 py-3 bg-gray-900 text-white">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-gray-300">MOSAIC District Rollup</div>
          <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {grand.schools || 0} schools · {grand.classes || 0} classes · {grand.intake_rows || 0} intake rows
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="text-gray-300 hover:text-white" data-testid="rollup-refresh">
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </header>

      <div className="divide-y divide-gray-200">
        {(data.schools || []).map((school) => {
          const open = openSchools[school.school_id] !== false; // default open
          return (
            <div key={school.school_id} data-testid={`rollup-school-${school.school_id}`}>
              <button
                type="button"
                onClick={() => setOpenSchools((s) => ({ ...s, [school.school_id]: !open }))}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
              >
                {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                <div className="flex-1">
                  <div className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{school.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {school.classes.length} classes · {school.totals.screened} screened · {school.totals.intake_rows} intake rows
                  </div>
                </div>
                <div className="w-40">
                  <DistroBar by_level={school.totals.by_level} screened={school.totals.screened} />
                </div>
                <div className="flex gap-1 text-[10px] uppercase tracking-wider w-44 justify-end">
                  {school.totals.flags.newcomer > 0 && <span className="px-1.5 py-0.5 border border-rose-300 bg-rose-50 text-rose-800 font-bold">NEW · {school.totals.flags.newcomer}</span>}
                  {school.totals.flags.sife > 0 && <span className="px-1.5 py-0.5 border border-violet-300 bg-violet-50 text-violet-800 font-bold">SIFE · {school.totals.flags.sife}</span>}
                  {school.totals.flags.iep_504 > 0 && <span className="px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 font-bold">IEP · {school.totals.flags.iep_504}</span>}
                </div>
              </button>

              {open && (
                <div className="bg-gray-50 px-4 pb-3">
                  <table className="w-full text-xs" data-testid={`rollup-classes-${school.school_id}`}>
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-1.5">Class</th>
                        <th>Grade</th>
                        <th>Students</th>
                        <th>Screened</th>
                        <th>Avg Composite</th>
                        <th>Distribution</th>
                        <th className="text-right">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {school.classes.map((c) => (
                        <tr key={c.class_id} className="border-b border-gray-100" data-testid={`rollup-class-${c.class_id}`}>
                          <td className="py-1.5 font-semibold">{c.name || c.class_id}</td>
                          <td>{c.grade_level || '—'}</td>
                          <td className="tabular-nums text-gray-600">{c.student_count}</td>
                          <td className="tabular-nums text-gray-600">{c.screened}</td>
                          <td>
                            {c.avg_composite > 0 ? (
                              <span className="inline-flex items-center gap-1">
                                <span className="tabular-nums">{c.avg_composite}</span>
                                <MosaicBucketBadge level={Math.max(1, Math.min(6, Math.round(c.avg_composite)))} compact />
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="w-40">
                            <DistroBar by_level={c.by_level} screened={c.screened} />
                          </td>
                          <td className="text-right">
                            <span className="inline-flex gap-1">
                              {c.flags.newcomer > 0 && <span className="px-1 border border-rose-300 bg-rose-50 text-rose-800 font-bold">{c.flags.newcomer}N</span>}
                              {c.flags.sife > 0 && <span className="px-1 border border-violet-300 bg-violet-50 text-violet-800 font-bold">{c.flags.sife}S</span>}
                              {c.flags.iep_504 > 0 && <span className="px-1 border border-amber-300 bg-amber-50 text-amber-800 font-bold">{c.flags.iep_504}I</span>}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {school.classes.length === 0 && (
                        <tr><td colSpan={7} className="py-2 italic text-gray-400">No classes</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MosaicDistrictRollup;
